<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> Back </BaseButton>

        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon :kind="isSelfMode ? 'self' : 'sixty'" size="md" />
                <h2 class="title-subsection text-ink">{{ headerTitle }}</h2>
                <BaseChip v-if="!isSelfMode" size="2xs">Step 1 of 3</BaseChip>
            </div>
            <p class="scaffold-body-small text-ink-muted">{{ subtitle }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="loading" class="scaffold-body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <!-- The seeded template is global, so an empty list means active campaigns own every template, not that none are enabled. -->
        <BaseCard v-else-if="!templates.length" padding="md">
            <p class="body-small text-ink-muted">{{ t("reflections.start.startedFromUpcoming") }}</p>
        </BaseCard>
        <template v-else>
            <BaseCard v-if="templates.length > 1" padding="md">
                <div class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">Template</span>
                    <BaseSelect
                        v-model="selectedTemplateId"
                        aria-label="Template options"
                        :options="templateOptions"
                        trigger-class="border-border-strong! rounded-lgr! py-2! px-3! text-ink! scaffold-body-small"
                        content-class="border-border-strong! rounded-xl!" />
                    <span v-if="!isSelfMode && unlockNote" class="scaffold-details-small text-ink-muted">{{ unlockNote }}</span>
                </div>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" padding="md">
                <label for="self-assess" class="flex items-start gap-3 cursor-pointer">
                    <input id="self-assess" v-model="selfAssess" type="checkbox" class="mt-1 accent-control-accent" />
                    <span class="flex flex-col gap-1">
                        <span class="body-semibold text-ink">Also complete the assessment yourself</span>
                        <span class="scaffold-body-small text-ink-muted"> Lets you see how your view of yourself compares to others'. We'll show the gaps in your report. </span>
                    </span>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" padding="md">
                <label for="personal-note" class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">Personal note <span class="body text-ink-muted">(optional)</span></span>
                    <textarea
                        id="personal-note"
                        v-model="personalNote"
                        rows="3"
                        placeholder="Hey — I'd really value your honest take. It's anonymous, and it'll only take ~15 minutes. Thank you."
                        class="w-full px-3 py-2 border border-control-border rounded-lgr resize-none scaffold-body-small focus-field"></textarea>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode" theme="cream-muted" padding="md" class="flex items-start gap-3">
                <i class="bi bi-shield-check text-notification-success-text mt-1" aria-hidden="true"></i>
                <div class="flex flex-col gap-1">
                    <div class="body-semibold text-ink">Always anonymous</div>
                    <div class="scaffold-body-small text-ink-muted">
                        Results are aggregated. Cuts by relationship show only when 3+ people in that group respond — otherwise we combine everything.
                    </div>
                </div>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div class="flex justify-end">
                <BaseButton theme="primary" :disabled="creating" @click="onCreate">
                    {{ creating ? "Creating…" : isSelfMode ? "Start self reflection" : "Next" }}
                </BaseButton>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, StartCycleContextResponse, TemplateSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    // "self" opens the self-only flow (no rater fields, cycle auto-launches on create).
    // "360" is the full 360 setup (template + self-assess toggle + personal note).
    mode?: "self" | "360";
}

const { mode = "360" } = defineProps<Props>();

const emit = defineEmits<{
    (event: "back"): void;
    // Fired after the backend creates the cycle. Payload lets the parent
    // decide the next inline view — self-only cycles jump into the take flow,
    // 360 cycles jump into Nominate.
    (event: "created", payload: { cycleId: string; isSelfAssessmentOnly: boolean; selfAssessEnabled: boolean; title: string; closesAt: string | null }): void;
}>();

const { t } = useI18n();

const isSelfMode = computed(() => mode === "self");

const templates = ref<TemplateSummary[]>([]);
const selectedTemplateId = ref<string | null>(null);
const templateOptions = computed(() => templates.value.map((templateOption) => ({ key: templateOption.id, label: templateOption.name })));
const defaultMinResponses = ref<number | null>(null);
const selectedTemplate = computed(() => templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value) ?? null);
const minResponses = computed<number | null>(() => selectedTemplate.value?.min_responses ?? defaultMinResponses.value);
const questionCount = computed<number | null>(() => {
    const selected = templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value);
    return selected?.question_count ?? null;
});
const questionCountLabel = computed<string>(() => {
    const n = questionCount.value;
    if (n === null) {
        return "";
    }
    return n === 1 ? "1 short question" : `${n} short questions`;
});

const loading = ref(true);
const loadError = ref<string | null>(null);
const selfAssess = ref(true);
// Category thresholds replace the overall minimum, so neither the subtitle nor
// the picker note can promise an unlock here. The nomination step spells the
// thresholds out instead.
const hasCategoryThresholds = computed(() => (selectedTemplate.value?.category_thresholds?.thresholds?.length ?? 0) > 0);
const unlockNote = computed(() => (hasCategoryThresholds.value ? null : `Results unlock after ${minResponses.value}+ responses.`));

const personalNote = ref("");
const creating = ref(false);
const error = ref<string | null>(null);

const headerTitle = computed(() => (isSelfMode.value ? "Start a self reflection" : "Start a 360"));

const subtitle = computed(() => {
    if (isSelfMode.value) {
        return `Reflect on your own strengths and areas for growth. ${questionCountLabel.value}`;
    }
    if (hasCategoryThresholds.value) {
        return `Anonymous feedback from your peers, your manager, and direct reports. ${questionCountLabel.value}, ~15 minutes for raters.`;
    }
    if (minResponses.value === null) {
        return "Anonymous feedback from your peers, your manager, and direct reports. ~15 minutes for raters.";
    }
    if (minResponses.value === 0) {
        return `Anonymous feedback from your peers, your manager, and direct reports. ${questionCountLabel.value}, ~15 minutes for raters. Your report is ready as soon as you launch.`;
    }
    return `Anonymous feedback from your peers, your manager, and direct reports. ${questionCountLabel.value}, ~15 minutes for raters. Your report is ready when ${minResponses.value}+ raters respond.`;
});

onMounted(async () => {
    const startContextUrl = isSelfMode.value ? "/v1/reflections/start-context?mode=self" : "/v1/reflections/start-context";
    const { data, error: fetchError } = await useFetch(startContextUrl).json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || (isSelfMode.value ? "Could not start a self reflection." : "Could not start a 360.");
        return;
    }
    const ctx = (data.value as StartCycleContextResponse).context;
    defaultMinResponses.value = ctx.min_responses;
    templates.value = ctx.templates;
    selectedTemplateId.value = ctx.default_template_id ?? null;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_START_VIEWED, {
        template_count: ctx.templates.length,
        has_default_template: ctx.default_template_id !== null,
        mode: isSelfMode.value ? "self_assessment" : "360",
    });
});

async function onCreate() {
    if (creating.value) {
        return;
    }
    creating.value = true;
    error.value = null;
    try {
        const { data, error: fetchError } = await useFetch("/v1/reflections/cycles")
            .post(
                JSON.stringify({
                    template_id: selectedTemplateId.value,
                    is_self_assessment_only: isSelfMode.value,
                    self_assess_enabled: isSelfMode.value ? true : selfAssess.value,
                    personal_note: isSelfMode.value ? "" : personalNote.value,
                }),
                "application/json",
            )
            .json();

        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || "Could not create cycle. Please try again.";
            return;
        }

        const cycle = (data.value as CycleEnvelopeResponse).cycle;
        const templateName = templates.value.find((t) => t.id === selectedTemplateId.value)?.name ?? "Reflection";
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CYCLE_CREATED, {
            cycle_id: cycle.id,
            template_id: selectedTemplateId.value,
            is_self_assessment_only: isSelfMode.value,
            self_assess_enabled: isSelfMode.value ? true : selfAssess.value,
            has_personal_note: personalNote.value.trim().length > 0,
        });
        emit("created", {
            cycleId: cycle.id,
            isSelfAssessmentOnly: isSelfMode.value,
            selfAssessEnabled: isSelfMode.value ? true : selfAssess.value,
            title: templateName,
            closesAt: cycle.closes_at ?? null,
        });
    } finally {
        creating.value = false;
    }
}
</script>
